import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// import { motion } from 'framer-motion';
// import { Link } from 'react-router-dom';
import { RootState } from '../store';
import { setTrending, setRecent } from '../store/slices/cacheSlice';
import MovieCard from '../components/movie/MovieCard';
import HeroSlider from '../components/home/HeroSlider';
import RecommendCarousel from '../components/home/RecommendCarousel';
import styles from './Home.module.css';

import adsPlaceholderImg from '../assets/images/ads_300_250.png';

// Import JSON data
import dramaData from '../data/drama-full-1-short-dramas.json';

import { trackPageView } from '../utils/page-tracker';

const HOT_GENRES = [
  'Urban',
  'Revenge',
  'Domineering CEO',
  'Gangster',
  'Love',
  'Love triangle',
  'Suspense',
  'Legacy',
  'Love of trillionaire',
  'Murder'
];

// 为hotGenresData定义接口类型
interface GenreData {
  genre: string;
  cover: string;
  movieId: string;
}

const Home: React.FC = () => {
  const dispatch = useDispatch();
  const { trending, recent } = useSelector((state: RootState) => state.cache);

  // 新增More Recommended和Top Picks数据
  const [moreRecommended] = useState(dramaData.data.slice(25, 35));
  const [topPicks] = useState(dramaData.data.slice(35, 45));
  
  // 新增猜你喜欢数据
  const [mayLikeItems] = useState(dramaData.data.slice(45, 60));
  
  // 计算Hot Genres数据 - 使用state而非useState初始函数，确保每次均能获取到最新的横图
  const [hotGenresData, setHotGenresData] = useState<GenreData[]>([]);
  
  // 添加返回顶部功能
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // 监听滚动事件
  useEffect(() => {
    window.loadAllTask();
    trackPageView(); // 手动调用

    const handleScroll = () => {
      // 当页面滚动超过300px时显示返回顶部按钮
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // 返回顶部函数
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    // 生成Hot Genres数据，确保使用coverImageW并避免重复封面
    const usedMovieIds = new Set<string>();
    const genresData = HOT_GENRES.map(genre => {
      // 查找所有包含该类型的电影
      const matchingMovies = dramaData.data.filter(movie => 
        movie.genres && 
        movie.genres.some(g => 
          g.toLowerCase().includes(genre.toLowerCase()) || 
          genre.toLowerCase().includes(g.toLowerCase())
        ) && 
        movie.coverImageW && 
        !usedMovieIds.has(movie._id)
      );
      
      if (matchingMovies.length === 0) {
        // 如果没有找到未使用的电影，尝试不考虑usedMovieIds
        const anyMatchingMovie = dramaData.data.find(movie => 
          movie.genres && 
          movie.genres.some(g => 
            g.toLowerCase().includes(genre.toLowerCase()) || 
            genre.toLowerCase().includes(g.toLowerCase())
          ) && 
          movie.coverImageW
        );
        
        if (anyMatchingMovie) {
          usedMovieIds.add(anyMatchingMovie._id);
          return {
            genre,
            cover: anyMatchingMovie.coverImageW,
            movieId: anyMatchingMovie._id
          };
        }
        
        return { genre, cover: '', movieId: '' };
      }
      
      // 随机选择一个未使用的电影
      const randomIndex = Math.floor(Math.random() * matchingMovies.length);
      const selectedMovie = matchingMovies[randomIndex];
      usedMovieIds.add(selectedMovie._id);
      
      return {
        genre,
        cover: selectedMovie.coverImageW,
        movieId: selectedMovie._id
      };
    });
    
    setHotGenresData(genresData);
  }, []);

  useEffect(() => {
    // Trending Now模块直接取第6-15条数据
    if (trending.length === 0) {
      const trendingItems = dramaData.data.slice(5, 15);
      dispatch(setTrending(trendingItems));
    }

    // New Releases模块直接取第16-25条数据
    if (recent.length === 0) {
      const recentItems = dramaData.data.slice(15, 25);
      dispatch(setRecent(recentItems));
    }
  }, [dispatch, trending.length, recent.length]);

  const featuredItems = dramaData.data.filter(item => 
    item.categories.includes('New Release') && 
    item.categories.includes('Carousel')
  ).slice(0, 5);

  // Short Videos板块使用全部数据
  // const shortVideos = videoData.data;

  return (
    <div className={styles.home}>
      <div className={`${styles.heroSliderContainer} ${styles.halfMb}`}>
        <HeroSlider items={featuredItems} />
      </div>
      
      <div className={styles.adContainer}>
        <div id="seattle-ad-10001" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}>
          <div style={{marginBottom: '0.2rem'}} className="adTip">Advertisement ▼</div>
            <img src={adsPlaceholderImg} alt="Advertisement" />
          <div style={{marginTop: '0.2rem'}} className="adTip">Advertisement ▲</div>
        </div>
      </div>
      
      <section className={`${styles.section} ${styles.halfMt} ${styles.halfMb}`}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Trending Now</h2>
        </div>
        <div className={styles.movieGrid}>
          {trending.map((movie, index) => (
            <MovieCard key={movie._id} movie={movie} index={index} />
          ))}
        </div>
      </section>

      <div className={styles.adContainer}>
      <div id="seattle-ad-10002" style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center'}}>
          <div style={{marginBottom: '0.2rem'}} className="adTip">Advertisement ▼</div>
            <img src={adsPlaceholderImg} alt="Advertisement" />
          <div style={{marginTop: '0.2rem'}} className="adTip">Advertisement ▲</div>
        </div>
      </div>
      
      <section className={`${styles.section} ${styles.halfMt}`}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>New Releases</h2>
        </div>
        <div className={styles.movieGrid}>
          {recent.map((movie, index) => (
            <MovieCard key={movie._id} movie={movie} index={index} />
          ))}
        </div>
      </section>

      {/* More Recommended版块 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>More Recommended</h2>
        </div>
        <div className={styles.movieGrid}>
          {moreRecommended.map((movie, index) => (
            <MovieCard key={movie._id} movie={movie} index={index} />
          ))}
        </div>
      </section>

      {/* Top Picks版块 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Top Picks</h2>
        </div>
        <div className={styles.movieGrid}>
          {topPicks.map((movie, index) => (
            <MovieCard key={movie._id} movie={movie} index={index} />
          ))}
        </div>
      </section>
      
      {/* 猜你喜欢轮播模块 */}
      <section className={styles.section}>
        <RecommendCarousel items={mayLikeItems} title="You May Like" />
      </section>
      
      {/* <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Short Videos</h2>
        </div>
        <div className={styles.movieGrid}>
          {shortVideos.map((movie, index) => (
            <MovieCard key={movie._id} movie={movie} index={index} />
          ))}
        </div>
      </section> */}

      {/* Hot Genres版块 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Hot Genres</h2>
        </div>
        <div className={styles.hotGenresGrid}>
          {hotGenresData.map(({ genre, cover }) => (
            <a 
              href={`/search?genre=${encodeURIComponent(genre)}`} 
              key={genre} 
              className={styles.hotGenreItem}
            >
              {cover && (
                <div className={styles.hotGenreImgWrapper}>
                  <img src={cover} alt={genre} className={styles.hotGenreImg} />
                </div>
              )}
              <div className={styles.hotGenreName}>{genre}</div>
            </a>
          ))}
        </div>
      </section>

      {/* 返回顶部按钮 */}
      {showBackToTop && (
        <button 
          className={styles.backToTop} 
          onClick={scrollToTop}
          aria-label="返回顶部"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 12L12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}
    </div>
  );
};

export default Home; 